import React from 'react';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { GridActionsCellItem, GridEditInputCell, GridRowModes } from '@mui/x-data-grid-premium';
import { INTEGER } from 'utils/helpers/validationMessages';
import { isInt } from 'utils/helpers/formValidations';
import { Tooltip } from '@mui/material';
import ProductGradeChip from './component/ProductGradeChip';

const minWidth = 150;

const getValidationMessage = volume => {
  if (R.isNil(volume)) {
    return 'Volume cannot be empty';
  }
  if (R.not(isInt(volume))) {
    return INTEGER;
  }
  if (R.gt(1, volume)) {
    return 'Volume cannot be less than 1';
  }
  return '';
};

const NominatedColumns = ({
  handleCancel,
  nominations,
  rowModesModel,
  setNominations,
  setRowModesModel,
  submitNomination,
  handleCopyNomination,
  isProductShippingViewOnly,
}) => {
  const handleEdit = row => () => {
    const { nominationId, volNominated } = row;
    setRowModesModel({ ...rowModesModel, [nominationId]: { mode: GridRowModes.Edit } });
    setNominations({
      ...nominations,
      [nominationId]: { hasChanged: false, isValid: true, nominationId, volNominated },
    });
  };

  const handleSave = id => () => {
    submitNomination(nominations[id]);
  };

  return [
    {
      field: 'segmentName',
      flex: 1,
      headerName: 'Segment',
      minWidth,
    },
    {
      field: 'shipperName',
      flex: 1,
      headerName: 'Shipper',
      minWidth,
    },
    {
      aggregable: false,
      field: 'productName',
      flex: 1,
      headerName: 'Product Grade',
      minWidth,
      renderCell: ({ value }) => <ProductGradeChip value={value} />,
    },
    {
      align: 'left',
      editable: true,
      field: 'volNominated',
      flex: 1,
      headerAlign: 'left',
      headerName: 'Vol. Nominated (bbl)',
      minWidth,
      preProcessEditCellProps: params => {
        const { props, row } = params;

        const hasError = [
          R.isNil(props.value),
          R.not(isInt(props.value)),
          R.gt(1, props.value),
        ].includes(true);

        setNominations(
          R.mergeDeepLeft(
            {
              [row.nominationId]: {
                hasChanged: row.volNominated !== props.value,
                isValid: !hasError,
                nominationId: row.nominationId,
                volNominated: props.value,
              },
            },
            nominations
          )
        );
        return { ...params.props, error: hasError };
      },
      renderEditCell: params => {
        const { error, value } = params;
        const title = error && getValidationMessage(value);

        return (
          <Tooltip
            arrow
            open={error}
            slotProps={{
              popper: {
                sx: {
                  '& .MuiTooltip-arrow': {
                    color: 'error.main',
                  },
                  '& .MuiTooltip-tooltip': {
                    backgroundColor: 'error.main',
                    color: 'error.contrastText',
                  },
                },
              },
            }}
            sx={{
              '& .MuiTooltip-popper .MuiTooltip-arrow': {
                color: 'error.main',
              },
            }}
            title={title}
          >
            <Box sx={{ height: '100%' }}>
              <GridEditInputCell
                {...params}
                sx={{
                  '&.Mui-error': {
                    backgroundColor: 'error.light',
                    color: 'error.dark',
                  },
                  backgroundColor: 'secondary.light',
                  height: '100%',
                }}
              />
            </Box>
          </Tooltip>
        );
      },
      type: 'number',
    },
    {
      field: 'nomMonth',
      flex: 1,
      headerName: 'Nomination Month',
      minWidth,
    },
    {
      aggregable: false,
      disableExport: true,
      field: 'action',
      getActions: params => {
        const { row } = params;
        const { nominationId, nomMonth } = row;

        if (isProductShippingViewOnly || R.isNil(nominationId)) return [];

        const isInEditMode = rowModesModel[nominationId]?.mode === GridRowModes.Edit;

        return [
          ...(isInEditMode
            ? [
                <GridActionsCellItem
                  disabled={
                    !nominations[nominationId].hasChanged || !nominations[nominationId].isValid
                  }
                  icon={<SaveIcon />}
                  label="Save"
                  onClick={handleSave(nominationId)}
                />,
                <GridActionsCellItem
                  icon={<CancelIcon />}
                  label="Cancel"
                  onClick={handleCancel(nominationId)}
                />,
              ]
            : new Date() < new Date(nomMonth)
            ? [<Button onClick={handleEdit(row)}>Edit</Button>]
            : []),
          <GridActionsCellItem
            icon={<FileCopyIcon />}
            label="Copy"
            onClick={() => handleCopyNomination(nominationId)}
          />,
        ];
      },
      headerName: 'Copy',
      minWidth,
      type: 'actions',
    },
  ];
};

export default NominatedColumns;
